/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * A human mean is a person who is allocated to execute an activity or part of it. They are considered as a resource, just like other means of execution. Examples of human means include drivers, pilots, handlers, and operators.
 * @export
 * @interface HumanMean
 */
export interface HumanMean {
    /**
     * TODO: to be reviewed.
     * @type {Date}
     * @memberof HumanMean
     */
    allocationDate?: Date;
    /**
     * List of contacts related to the resource. They can be persons or teams who can be contacted regarding the resource.
     * @type {Array<Contact>}
     * @memberof HumanMean
     */
    contacts?: Array<Contact>;
    /**
     * External references of the resource should be used when they are defined in a legacy system that registers the resource under a different reference identifier than the URI. This is useful to search for the resource using the ID from the legacy app. Frequent ExternalReference Types:
     *                             - 'OWNER_ID'
     *                             - 'PROVIDER_ID'
     * @type {Array<ExternalReference>}
     * @memberof HumanMean
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * Actual link between resources, such as a trailer to a truck, a driver to a truck, a container to a vessel, etc. This view is not always manageable, the manager of the resource being  responsible for ensuring that the links between resources are continuously updated.
     * @type {Array<string>}
     * @memberof HumanMean
     */
    isLinkedToResources?: Array<string>;
    /**
     * Actual pieces allocated to a resource. This view is not always manageable, and the owner/manager of the resource is accountable for ensuring the content of the resource is updated continuously.
     * @type {Array<Link>}
     * @memberof HumanMean
     */
    pieces?: Array<Link>;
    /**
     * Special characteristics applied to the resource and to be shared between partners.
     * @type {Array<SpecialCharacteristic>}
     * @memberof HumanMean
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Special instructions applied to the resource and to be shared with a provider.
     * @type {Array<SpecialInstruction>}
     * @memberof HumanMean
     */
    specialInstructions?: Array<SpecialInstruction>;
    /**
     * Free text describing the human mean.
     * @type {string}
     * @memberof HumanMean
     */
    description?: string;
    /**
     * Role of the human mean allocated. The list of the roles is described in the ontology...
     *                
     * @type {string}
     * @memberof HumanMean
     */
    role?: string;
}

/**
 * Check if a given object implements the HumanMean interface.
 */
export function instanceOfHumanMean(value: object): value is HumanMean {
    return true;
}

export function HumanMeanFromJSON(json: any): HumanMean {
    return HumanMeanFromJSONTyped(json, false);
}

export function HumanMeanFromJSONTyped(json: any, ignoreDiscriminator: boolean): HumanMean {
    if (json == null) {
        return json;
    }
    return {
        
        'allocationDate': json['allocationDate'] == null ? undefined : (new Date(json['allocationDate'])),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'isLinkedToResources': json['isLinkedToResources'] == null ? undefined : json['isLinkedToResources'],
        'pieces': json['pieces'] == null ? undefined : ((json['pieces'] as Array<any>).map(LinkFromJSON)),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
        'description': json['description'] == null ? undefined : json['description'],
        'role': json['role'] == null ? undefined : json['role'],
    };
}

export function HumanMeanToJSON(json: any): HumanMean {
    return HumanMeanToJSONTyped(json, false);
}

export function HumanMeanToJSONTyped(value?: HumanMean | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'allocationDate': value['allocationDate'] == null ? value['allocationDate'] : value['allocationDate'].toISOString(),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'isLinkedToResources': value['isLinkedToResources'],
        'pieces': value['pieces'] == null ? undefined : ((value['pieces'] as Array<any>).map(LinkToJSON)),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
        'description': value['description'],
        'role': value['role'],
    };
}

